import React, { useEffect, useMemo, useRef, useState } from 'react';

type WidgetId = 'attendance' | 'fees' | 'exams' | 'alerts' | 'ai';
type ViewId = 'overview' | 'performance' | 'reports';

type WidgetDef = {
  id: WidgetId;
  label: string;
  hint: string;
};

const WIDGETS: WidgetDef[] = [
  { id: 'attendance', label: 'Attendance pulse', hint: 'Live campus presence' },
  { id: 'fees', label: 'Fee collection', hint: 'Term progress' },
  { id: 'exams', label: 'Exam readiness', hint: 'Admit cards & marks' },
  { id: 'alerts', label: 'Smart alerts', hint: 'Gate, WhatsApp, fees' },
  { id: 'ai', label: 'AI brief', hint: 'Daily principal summary' },
];

const VIEWS: Array<{ id: ViewId; label: string; lead: string; hint: string }> = [
  {
    id: 'overview',
    label: 'Overview',
    hint: 'Pin your home',
    lead: 'Pin the widgets you care about — your home screen, not a generic dashboard.',
  },
  {
    id: 'performance',
    label: 'Performance',
    hint: 'Class trends',
    lead: 'Compare classes and subjects in one glance with live trend lines.',
  },
  {
    id: 'reports',
    label: 'Reports',
    hint: 'One-page pack',
    lead: 'Build a one-page report pack and export when you are ready.',
  },
];

const CLASS_BARS = [
  { name: '12-Sci', score: 94, note: 'Strong' },
  { name: '10-A', score: 92, note: 'Strong' },
  { name: '7-A', score: 88, note: 'Steady' },
  { name: '9-B', score: 86, note: 'Steady' },
  { name: '8-C', score: 78, note: 'Watch' },
];

const REPORT_PACKS = [
  { id: 'fee', label: 'Fee follow-ups', detail: 'Class-wise pending collections' },
  { id: 'att', label: 'Attendance gaps', detail: 'Students needing a gentle follow-up' },
  { id: 'admit', label: 'Admit-card readiness', detail: 'Fee-blocked vs ready to print' },
  { id: 'gate', label: 'Gate pickup log', detail: 'Today’s handovers and PIN uses' },
  { id: 'teach', label: 'Teaching overview', detail: 'Class and subject outcomes' },
  { id: 'ai', label: 'AI weekly brief', detail: 'Risks, wins, and next actions' },
];

function formatClock(d: Date) {
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
}

export function LiveCommandCenter() {
  const [view, setView] = useState<ViewId>('overview');
  const [enabled, setEnabled] = useState<Record<WidgetId, boolean>>({
    attendance: true,
    fees: true,
    exams: true,
    alerts: true,
    ai: true,
  });
  const [selectedReports, setSelectedReports] = useState<string[]>(['fee', 'admit', 'gate', 'ai']);
  const [pulse, setPulse] = useState(0);
  const [now, setNow] = useState(() => new Date());
  const rootRef = useRef<HTMLDivElement>(null);
  const [onScreen, setOnScreen] = useState(false);
  const [pageVisible, setPageVisible] = useState(
    () => typeof document === 'undefined' || !document.hidden
  );

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const onVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // This is a decorative demo, so it only ticks while someone can actually see
  // it — otherwise it re-renders every 1.8s for the whole visit.
  useEffect(() => {
    if (!onScreen || !pageVisible) return;
    setNow(new Date());
    const id = window.setInterval(() => {
      setPulse((p) => (p + 1) % 100);
      setNow(new Date());
    }, 1800);
    return () => window.clearInterval(id);
  }, [onScreen, pageVisible]);

  const activeWidgets = useMemo(() => WIDGETS.filter((w) => enabled[w.id]), [enabled]);
  const activeView = VIEWS.find((v) => v.id === view) ?? VIEWS[0];
  const pinnedCount = activeWidgets.length;

  const attendancePct = 92 + Math.round(Math.sin(pulse / 12) * 2);
  const feePct = 78 + Math.round(Math.cos(pulse / 14) * 3);
  const examPct = 89 + Math.round(Math.sin(pulse / 10) * 2);
  const busOnRoad = 14;
  const pickups = 186 + (pulse % 3);

  const toggleWidget = (id: WidgetId) => {
    setEnabled((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const onCount = Object.values(next).filter(Boolean).length;
      if (onCount === 0) return prev;
      return next;
    });
  };

  const toggleReport = (id: string) => {
    setSelectedReports((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="live-cc" ref={rootRef}>
      <div className="live-cc__frame">
        <div className="live-cc__chrome" aria-hidden>
          <span className="live-cc__chrome-dot" />
          <span className="live-cc__chrome-dot" />
          <span className="live-cc__chrome-dot" />
          <span className="live-cc__chrome-path">skoolfix · command center</span>
          <span className="live-cc__chrome-live">
            <span className="live-cc__chrome-pulse" />
            Live · {formatClock(now)}
          </span>
        </div>

        <div className="live-cc__status" aria-label="Campus snapshot">
          <div className="live-cc__status-item">
            <span className="live-cc__status-value">{attendancePct}%</span>
            <span className="live-cc__status-label">Present</span>
          </div>
          <div className="live-cc__status-item">
            <span className="live-cc__status-value">{feePct}%</span>
            <span className="live-cc__status-label">Fees</span>
          </div>
          <div className="live-cc__status-item">
            <span className="live-cc__status-value">{pickups}</span>
            <span className="live-cc__status-label">Pickups</span>
          </div>
          <div className="live-cc__status-item">
            <span className="live-cc__status-value">{busOnRoad}</span>
            <span className="live-cc__status-label">Buses on road</span>
          </div>
        </div>

        <div className="live-cc__toolbar" role="tablist" aria-label="Dashboard views">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={view === v.id}
              className={`live-cc__view-btn${view === v.id ? ' live-cc__view-btn--active' : ''}`}
              onClick={() => setView(v.id)}
            >
              <span className="live-cc__view-label">{v.label}</span>
              <span className="live-cc__view-hint">{v.hint}</span>
            </button>
          ))}
        </div>

        <div className="live-cc__lead-row">
          <p className="live-cc__lead">{activeView.lead}</p>
          {view === 'overview' && (
            <p className="live-cc__lead-meta" aria-live="polite">
              {pinnedCount} widget{pinnedCount === 1 ? '' : 's'} pinned
            </p>
          )}
        </div>

        <div className="live-cc__stage" key={view}>
          {view === 'overview' && (
            <div className="live-cc__overview">
              <aside className="live-cc__palette" aria-label="Customize widgets">
                <p className="live-cc__palette-title">Customize home</p>
                <p className="live-cc__palette-hint">Toggle widgets — the board updates instantly.</p>
                <ul className="live-cc__palette-list">
                  {WIDGETS.map((w) => (
                    <li key={w.id}>
                      <button
                        type="button"
                        className={`live-cc__chip${enabled[w.id] ? ' live-cc__chip--on' : ''}`}
                        aria-pressed={enabled[w.id]}
                        onClick={() => toggleWidget(w.id)}
                      >
                        <span className="live-cc__chip-dot" aria-hidden />
                        <span>
                          <span className="live-cc__chip-label">{w.label}</span>
                          <span className="live-cc__chip-hint">{w.hint}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </aside>

              <div className="live-cc__board" aria-live="polite">
                {activeWidgets.length === 0 && (
                  <p className="live-cc__board-empty">Pin at least one widget to build your overview.</p>
                )}
                {activeWidgets.map((w, i) => (
                  <article
                    key={w.id}
                    className={`live-cc__widget live-cc__widget--${w.id}`}
                    style={{ animationDelay: `${0.04 + i * 0.05}s` }}
                  >
                    {w.id === 'attendance' && (
                      <>
                        <header className="live-cc__widget-head">
                          <h3>Attendance pulse</h3>
                          <span className="live-cc__live">Live</span>
                        </header>
                        <div className="live-cc__hero-metric">
                          <p className="live-cc__metric">
                            <span className="live-cc__metric-value">{attendancePct}%</span>
                            <span className="live-cc__metric-label">present today</span>
                          </p>
                          <div
                            className="live-cc__gauge"
                            style={{ ['--pct' as string]: String(attendancePct) }}
                            aria-hidden
                          >
                            <svg viewBox="0 0 36 36">
                              <circle className="live-cc__gauge-track" cx="18" cy="18" r="15.5" />
                              <circle
                                className="live-cc__gauge-fill"
                                cx="18"
                                cy="18"
                                r="15.5"
                                strokeDasharray={`${attendancePct} 100`}
                              />
                            </svg>
                          </div>
                        </div>
                        <p className="live-cc__widget-foot">Homeroom · period 1 closed for 42 / 48 sections</p>
                      </>
                    )}
                    {w.id === 'fees' && (
                      <>
                        <header className="live-cc__widget-head">
                          <h3>Fee collection</h3>
                        </header>
                        <p className="live-cc__metric">
                          <span className="live-cc__metric-value">{feePct}%</span>
                          <span className="live-cc__metric-label">term target</span>
                        </p>
                        <div className="live-cc__bar-track">
                          <div className="live-cc__bar-fill" style={{ width: `${feePct}%` }} />
                        </div>
                        <p className="live-cc__widget-foot">₹18.4L collected · ₹2.1L pending</p>
                      </>
                    )}
                    {w.id === 'exams' && (
                      <>
                        <header className="live-cc__widget-head">
                          <h3>Exam readiness</h3>
                        </header>
                        <p className="live-cc__metric">
                          <span className="live-cc__metric-value">{examPct}%</span>
                          <span className="live-cc__metric-label">admit cards ready</span>
                        </p>
                        <ul className="live-cc__mini-list">
                          <li>
                            <span>Fee-gated campaign</span>
                            <strong>Live</strong>
                          </li>
                          <li>
                            <span>Blocked on dues</span>
                            <strong>117</strong>
                          </li>
                        </ul>
                      </>
                    )}
                    {w.id === 'alerts' && (
                      <>
                        <header className="live-cc__widget-head">
                          <h3>Smart alerts</h3>
                          <span className="live-cc__count">3</span>
                        </header>
                        <ul className="live-cc__alert-list">
                          <li>WhatsApp fee blast · cost ₹420 ready</li>
                          <li>3 gate pickups need guardian match</li>
                          <li>Route East-1 delayed on parent map</li>
                        </ul>
                      </>
                    )}
                    {w.id === 'ai' && (
                      <>
                        <header className="live-cc__widget-head">
                          <h3>AI brief</h3>
                          <span className="live-cc__ai-tag">AI</span>
                        </header>
                        <p className="live-cc__ai-copy">
                          Focus on Class 8-C maths this week — scores dipped 6 pts. Clear 117 fee blocks before
                          admit-card print day; WhatsApp reminders could recover ~₹48k by Friday.
                        </p>
                      </>
                    )}
                  </article>
                ))}
              </div>
            </div>
          )}

          {view === 'performance' && (
            <div className="live-cc__perf">
              <div className="live-cc__perf-chart" aria-label="Class performance comparison">
                <div className="live-cc__perf-head">
                  <p className="live-cc__perf-eyebrow">Class outcomes</p>
                  <h3 className="live-cc__perf-title">This week’s pulse</h3>
                </div>
                {CLASS_BARS.map((row, i) => {
                  const wobble = Math.round(Math.sin((pulse + i * 8) / 15) * 2);
                  const value = Math.min(99, Math.max(60, row.score + wobble));
                  const watch = row.note === 'Watch';
                  return (
                    <div
                      key={row.name}
                      className={`live-cc__perf-row${watch ? ' live-cc__perf-row--watch' : ''}`}
                      style={{ animationDelay: `${0.05 + i * 0.05}s` }}
                    >
                      <span className="live-cc__perf-rank" aria-hidden>
                        {i + 1}
                      </span>
                      <span className="live-cc__perf-name">{row.name}</span>
                      <div className="live-cc__perf-track">
                        <div className="live-cc__perf-fill" style={{ width: `${value}%` }} />
                      </div>
                      <span className="live-cc__perf-score">{value}%</span>
                      <span className="live-cc__perf-note">{row.note}</span>
                    </div>
                  );
                })}
              </div>
              <aside className="live-cc__perf-insight">
                <p className="live-cc__ai-tag">AI insight</p>
                <h3>Where to intervene</h3>
                <p>
                  Class 8-C is trailing peers in Maths. Assign a 20-question AI practice set and schedule a
                  remedial block — predicted recovery: +5–7 pts next unit test.
                </p>
                <ul className="live-cc__insight-list">
                  <li>Practice set from AI bank</li>
                  <li>Remedial block this week</li>
                  <li>Parent note optional</li>
                </ul>
                <button type="button" className="live-cc__action">
                  Generate practice set
                </button>
              </aside>
            </div>
          )}

          {view === 'reports' && (
            <div className="live-cc__reports">
              <div className="live-cc__reports-head">
                <div>
                  <p className="live-cc__perf-eyebrow">Report builder</p>
                  <h3 className="live-cc__perf-title">Today&apos;s overview pack</h3>
                </div>
                <p className="live-cc__palette-hint">Select packs to include in the PDF.</p>
              </div>
              <ul className="live-cc__report-grid">
                {REPORT_PACKS.map((pack) => {
                  const on = selectedReports.includes(pack.id);
                  return (
                    <li key={pack.id}>
                      <button
                        type="button"
                        className={`live-cc__report${on ? ' live-cc__report--on' : ''}`}
                        aria-pressed={on}
                        onClick={() => toggleReport(pack.id)}
                      >
                        <span className="live-cc__report-check" aria-hidden />
                        <span>
                          <span className="live-cc__report-label">{pack.label}</span>
                          <span className="live-cc__report-detail">{pack.detail}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="live-cc__report-summary">
                <p>
                  <strong>{selectedReports.length}</strong> pack{selectedReports.length === 1 ? '' : 's'} ready for
                  preview
                </p>
                <button type="button" className="live-cc__action" disabled={selectedReports.length === 0}>
                  Preview report pack
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="live-cc__footnote">
        Interactive preview — on your campus portal, widgets use live school data and your role.
      </p>
    </div>
  );
}
